import { firstValueFrom } from "rxjs";
import HttpService from "./HttpService";
import { url } from "./APIEndPoints";
import { buildParamString, IAPIResponse } from "@mindmemobile/mm-shared-global";
import { IAPIPaymentReceiptViewModel } from "../interfaces";

/**
 * Fetches the receipt behind a public receipt token, for the /payment-receipt/:token page that the payment
 * receipt email's "Download PDF" button opens.
 *
 * The endpoint is anonymous by design - there is no session when someone follows a link out of
 * their inbox - and the token in the URL is the only credential. Nothing here should ever accept a
 * payment id instead: ids are enumerable, tokens are not.
 */
class ReceiptService {
  private httpService: HttpService;

  constructor() {
    this.httpService = new HttpService(
      undefined,
      `${process.env.REACT_APP_API_KEY}`,
      process.env.REACT_APP_API_BASE_URL,
    );
  }

  /**
   * The receipt for a token, or null when the token is unknown / no longer resolvable. The page
   * shows a plain "receipt not available" for null rather than distinguishing the reasons.
   */
  async getReceiptByToken(
    token: string,
  ): Promise<IAPIPaymentReceiptViewModel | null> {
    if (!token || !token.trim()) {
      return null;
    }
    try {
      const params: string = buildParamString({ token: token.trim() });
      const response = await firstValueFrom(
        this.httpService.get<IAPIResponse<IAPIPaymentReceiptViewModel>>(
          url.getReceiptByToken,
          params,
        ),
      );
      return response?.result ?? null;
    } catch {
      return null;
    }
  }
}

export default new ReceiptService();
